import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { TYPE_MAP } from "./equipmentTypes";
import PatientSpecFields from "./PatientSpecFields";

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-zinc-600">{label}</Label>
    {children}
  </div>
);

export default function EditPatientDialog({ patient, onClose, onSaved }) {
  const type = TYPE_MAP[patient.equipment_type] || TYPE_MAP.laptop;
  const [form, setForm] = useState({
    brand: patient.brand || "",
    model: patient.model || "",
    equipment_subtype: patient.equipment_subtype || "",
    serial_number: patient.serial_number || "",
    processor: patient.processor || "",
    ram: patient.ram || "",
    storage: patient.storage || "",
    color: patient.color || "",
    aesthetic_condition: patient.aesthetic_condition || "",
    includes_charger: !!patient.includes_charger,
    includes_battery: !!patient.includes_battery,
    issue: patient.issue || "",
    observations: patient.observations || "",
    supplier: patient.supplier || "",
    batch_number: patient.batch_number || "",
    specs: { ...(patient.specs || {}) },
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const submit = async () => {
    if (!form.brand.trim() || !form.model.trim()) { toast.error("Marca y modelo son obligatorios"); return; }
    setSaving(true);
    try {
      const specs = { ...form.specs };
      if (!specs.has_gpu) { specs.has_gpu = false; specs.gpu_model = ""; specs.gpu_memory = ""; }
      const payload = { ...form, specs, aesthetic_condition: form.aesthetic_condition || null };
      if (payload.aesthetic_condition === null) delete payload.aesthetic_condition;
      const { data } = await api.patch(`/patients/${patient.id}`, payload);
      toast.success("Datos del equipo actualizados");
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilSimple size={18} weight="bold" className="text-[#002FA7]" />
            Editar datos del equipo <span className="font-mono text-[#002FA7]">{patient.code}</span>
            <span className="text-xs font-normal text-zinc-500">· {type.label}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Edita el problema, los datos base y la ficha técnica del equipo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 border-l-4 border-l-[#E0000F] bg-amber-50/60 p-3">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-amber-800">¿Por qué está aquí? · Problema / motivo</Label>
            <textarea value={form.issue} onChange={(e) => set("issue", e.target.value)} rows={3} placeholder="Ej: No enciende; le falta disipador y procesador; pantalla con líneas."
              className="w-full text-sm border border-amber-300 bg-white rounded-md p-2 mt-1 focus:outline-none focus:border-[#002FA7] resize-none" data-testid="edit-pat-issue" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {type.subtypes && (
              <Field label="Subtipo">
                <Select value={form.equipment_subtype || "none"} onValueChange={(v) => set("equipment_subtype", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="edit-pat-subtype"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {type.subtypes.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Marca *"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="edit-pat-brand" /></Field>
            <Field label="Modelo *"><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="edit-pat-model" /></Field>
            <Field label="N° de serie"><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} data-testid="edit-pat-serial" /></Field>
            <Field label="Condición estética">
              <Select value={form.aesthetic_condition || "none"} onValueChange={(v) => set("aesthetic_condition", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="edit-pat-aesthetic"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin definir</SelectItem>
                  <SelectItem value="A">A — Como nuevo</SelectItem>
                  <SelectItem value="B">B — Buen estado</SelectItem>
                  <SelectItem value="C">C — Con detalles</SelectItem>
                  <SelectItem value="D">D — Muy usado</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Proveedor"><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="edit-pat-supplier" /></Field>
            <Field label="N° de lote"><Input value={form.batch_number} onChange={(e) => set("batch_number", e.target.value)} data-testid="edit-pat-batch" /></Field>
          </div>

          {type.hasGpuGroup && (
            <div className="border border-zinc-200 rounded-md bg-zinc-50/50 p-3" data-testid="edit-gpu-group">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <Label className="text-xs text-zinc-700 font-semibold">Tarjeta gráfica</Label>
                  <div className="text-[11px] text-zinc-500">¿El equipo trae GPU dedicada o integrada con memoria?</div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <Checkbox checked={!!form.specs.has_gpu} onCheckedChange={(v) => setSpec("has_gpu", !!v)} data-testid="edit-pat-has-gpu" />
                  <span className="font-semibold">{form.specs.has_gpu ? "Sí, tiene" : "No tiene"}</span>
                </label>
              </div>
              {form.specs.has_gpu && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Modelo / chip"><Input value={form.specs.gpu_model || ""} onChange={(e) => setSpec("gpu_model", e.target.value)} placeholder="ej. GTX 1650, RTX 3060, Intel UHD 620" data-testid="edit-pat-gpu-model" /></Field>
                  <Field label="Memoria (VRAM)"><Input value={form.specs.gpu_memory || ""} onChange={(e) => setSpec("gpu_memory", e.target.value)} placeholder="ej. 2GB, 4GB, 12GB" data-testid="edit-pat-gpu-memory" /></Field>
                </div>
              )}
            </div>
          )}

          <div className="border border-zinc-200 rounded-md bg-white p-3">
            <PatientSpecFields form={form} set={set} setSpec={setSpec} equipmentType={patient.equipment_type} testidPrefix="edit-pat-spec" showMissing />
          </div>

          {(type.charger || type.battery || type.cable) && (
            <div className="flex gap-4 flex-wrap">
              {type.charger && <label className="inline-flex items-center gap-2 text-sm"><Checkbox checked={form.includes_charger} onCheckedChange={(v) => set("includes_charger", !!v)} data-testid="edit-pat-charger" /> Incluye cargador</label>}
              {type.battery && <label className="inline-flex items-center gap-2 text-sm"><Checkbox checked={form.includes_battery} onCheckedChange={(v) => set("includes_battery", !!v)} data-testid="edit-pat-battery" /> Incluye batería</label>}
              {type.cable && <label className="inline-flex items-center gap-2 text-sm"><Checkbox checked={!!form.specs.includes_cable} onCheckedChange={(v) => setSpec("includes_cable", !!v)} data-testid="edit-pat-cable" /> Incluye cable</label>}
            </div>
          )}

          <div>
            <Label className="text-xs text-zinc-500">Observaciones adicionales</Label>
            <textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} rows={3}
              className="w-full text-sm border border-zinc-200 rounded-md p-2 mt-1 focus:outline-none focus:border-[#002FA7]" data-testid="edit-pat-observations" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="edit-pat-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="edit-pat-save">{saving ? "Guardando…" : "Guardar cambios"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
